import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, FileDown, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import Logo from "@/components/Logo";
import { useBrochureGate } from "@/components/BrochureGate";
import api from "@/lib/api";

const SOCIALS = [
  { key: "instagram_url", label: "Instagram", Icon: Instagram },
  { key: "facebook_url", label: "Facebook", Icon: Facebook },
  { key: "youtube_url", label: "YouTube", Icon: Youtube },
  { key: "linkedin_url", label: "LinkedIn", Icon: Linkedin },
];

export default function Footer() {
  const { openGate } = useBrochureGate();
  const [social, setSocial] = useState({});

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSocial(data || {})).catch(() => {});
  }, []);

  const activeSocials = SOCIALS.filter((s) => (social[s.key] || "").trim());

  return (
    <footer id="contact" className="bg-[#202124] pt-16 pb-8 text-[#c9c7c1]" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo variant="horizontal" wordColor="#ffffff" subColor="#e79a5b" arcColor="#ffffff" sproutColor="#8fb89d" />
            <p className="mt-5 max-w-md text-sm leading-relaxed text-[#9a988f]">
              A next-generation Ayurvedic &amp; Nutraceutical manufacturing and PCD franchise company — uniting authentic Ayurveda, modern science and world-class GMP manufacturing. Breathe Better. Live Better.
            </p>
            <a href="#franchise" className="mt-6 inline-flex rounded-full bg-[#d46a1d] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#c05f16]" data-testid="footer-cta">
              Apply for Franchise
            </a>

            {activeSocials.length > 0 && (
              <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
                {activeSocials.map(({ key, label, Icon }) => (
                  <a
                    key={key}
                    href={social[key]}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-[#c9c7c1] transition-all hover:-translate-y-0.5 hover:border-[#d46a1d] hover:bg-[#d46a1d] hover:text-white"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-[#e79a5b]">Contact</div>
            <div className="mt-4 space-y-3 text-sm">
              <a href="mailto:wellnesssaans@gmail.com" className="flex items-center gap-2 hover:text-white" data-testid="footer-email"><Mail size={15} /> wellnesssaans@gmail.com</a>
              <a href="tel:+919920223329" className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone size={15} /> +91 99202 23329</a>
              <div className="flex items-start gap-2"><MapPin size={15} className="mt-0.5" /> <span>30/1 Akash Tower, Arhat Bazaar,<br/>Dehradun, India</span></div>
              <a href="https://www.swarnimakash.com" target="_blank" rel="noreferrer" className="block pt-1 text-[#9a988f] hover:text-white" data-testid="footer-parent">Manufactured by Swarnimakash Lifesciences</a>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-[#e79a5b]">Explore</div>
            <div className="mt-4 space-y-2.5 text-sm">
              <Link to="/franchise/ayurvedic-pcd" className="block hover:text-white">PCD Franchise</Link>
              <Link to="/franchise/uttarakhand" className="block hover:text-white" data-testid="footer-uttarakhand">Franchise in Uttarakhand</Link>
              <Link to="/manufacturing/third-party" className="block hover:text-white">Third-Party Mfg</Link>
              <Link to="/manufacturing/nutraceutical" className="block hover:text-white">Nutraceutical Mfg</Link>
              <Link to="/blog" className="block hover:text-white" data-testid="footer-blog">Blog</Link>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-[#e79a5b]">Company</div>
            <div className="mt-4 space-y-2.5 text-sm">
              <a href="/#offers" className="block hover:text-white">Offers</a>
              <a href="/#franchise" className="block hover:text-white">Franchise</a>
              <button onClick={() => openGate("brochure")} className="flex items-center gap-1.5 hover:text-white" data-testid="footer-brochure"><FileDown size={14} /> Brochure</button>
              <Link to="/admin" className="block hover:text-white" data-testid="footer-admin-link">Admin Login</Link>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-[#8a887f] md:flex-row">
          <div>© {new Date().getFullYear()} Saans Wellness Pvt Ltd. All rights reserved.</div>
          <div>WHO-GMP · ISO 9001:2015 · FSSAI · AYUSH Approved</div>
        </div>
      </div>
    </footer>
  );
}
